/**
 * Client ของ BOM และ Routing — พูดกับ `/v1/products/{product_id}/{bom|routing}`
 * ตาม payload contract ที่ freeze ไว้ใน `docs/API_CONTRACT.md`
 *
 * หลักการเดียวกับ `lib/products/client.ts` และ `lib/permissions/client.ts`:
 * - ฟังก์ชันในไฟล์นี้ **ไม่ throw** ความล้มเหลวทุกแบบกลายเป็นสถานะที่เรียกดูได้
 *   ผู้เรียกจึงเผลอ `catch` แล้วเดาเป็น "สำเร็จ" ไม่ได้
 * - payload ที่ผิดรูปคือ `invalid-response` ไม่ใช่ "ยังไม่มีสูตร" — สองอย่างนี้ต้อง
 *   แยกกันให้ขาด ไม่งั้นผู้ใช้จะอ่านว่า "ยังไม่ได้เขียน BOM" แล้วเขียนทับของที่มีอยู่
 * - **ไม่มี fallback ที่แต่งข้อมูลขึ้นมาเอง** สูตรที่หน้าจอเดาขึ้นมาเองคือใบสั่งผลิต
 *   ที่ผลิตของผิด
 * - **ไม่เขียนอะไรลง storage เลย** (D-049 อนุญาตเฉพาะ session token) สูตรที่ cache
 *   ไว้คือภาพเก่าของสิ่งที่วิศวกรอาจเพิ่งเขียน revision ใหม่ทับไปแล้ว
 *
 * BOM กับ Routing ใช้โค้ดชุดเดียวกันผ่าน `FormulaSpec` เพราะ contract เขียนไว้ว่า
 * "กติกาเดียวกับ BOM ทุกข้อ" — สองชุดที่เหมือนกัน 90% คือวิธีที่กติกาข้อหนึ่งจะถูก
 * แก้ที่เดียวแล้วอีกที่ค้างไว้เงียบ ๆ
 *
 * ⚠️ ไม่ใช่ด่านความปลอดภัย เซิร์ฟเวอร์เป็นผู้ตัดสินทุก request เสมอ
 * (`references/authorization-rbac.md` ข้อ 5)
 */

import type {
  CreateFormulaBody,
  CurrentFormula,
  FormulaRevision,
  FormulaSpec,
} from './types';
import { apiFetch } from '../auth/authorizedFetch';
import { buildApiUrl, getConfiguredApiBaseUrl } from '../apiBase';

/**
 * เส้นทางจริงของกลุ่มนี้ — ตาราง endpoint ใน contract ตัด base prefix ออก แต่ URL
 * ที่ client เรียกขึ้นต้นด้วย `/v1` เสมอ เหมือน `PRODUCTS_PATH`
 *
 * ใช้ `product_id` (UUID surrogate, D-039) **ห้ามใช้ `product_code`** เพราะรหัส
 * ธุรกิจเปลี่ยนได้ — contract ระบุข้อนี้ไว้ตรง ๆ กับทั้งกลุ่ม `/v1/products`
 */
export function formulaPath(productId: string, segment: string): string {
  return `/v1/products/${encodeURIComponent(productId)}/${segment}`;
}

export function formulaRevisionsPath(productId: string, segment: string): string {
  return `${formulaPath(productId, segment)}/revisions`;
}

/**
 * `plant_code` ของ `GET` เดินทางไปเป็น query string
 *
 * ⚠️ contract ระบุว่า `plant_code` **บังคับเสมอ** และเป็นขอบเขตสิทธิ์ (D-004) แต่
 * ไม่ได้เขียนว่า `GET` รับมันมาทางไหน — query string เป็นทางเดียวที่ `GET` มีให้
 * รายงานเป็น blocker แล้ว และถ้าเซิร์ฟเวอร์ตัดสินเป็นอย่างอื่น ต้องแก้ที่ contract
 * ก่อนแล้วทั้งสองฝั่งค่อยตาม
 */
export function buildPlantQuery(plantCode: string): string {
  const params = new URLSearchParams();
  params.append('plant_code', plantCode);
  return `?${params.toString()}`;
}

/** Envelope ของ error ตาม `api/errors.py` (`error_code`, `message`, `details`) */
export interface ApiErrorEnvelope {
  readonly error_code: string | null;
  readonly message: string | null;
  /**
   * `details.rule` — วิธีที่ contract ใช้แยกกรณีทางธุรกิจออกจากกันโดยไม่เพิ่ม
   * top-level error code ใหม่ หน้าจอต้องแสดงค่านี้ ไม่ใช่กลืนเป็น "ผิดพลาด" ทั่วไป
   * เพราะผู้ใช้ต้องรู้ว่าชนกฎข้อไหนจึงจะแก้ถูก
   */
  readonly rule: string | null;
  readonly details: Readonly<Record<string, unknown>>;
}

const EMPTY_ENVELOPE: ApiErrorEnvelope = {
  error_code: null,
  message: null,
  rule: null,
  details: {},
};

/**
 * ความล้มเหลวทุกแบบที่ contract นิยามไว้ แยกเป็นสถานะของตัวเอง
 *
 * ไม่ยุบ 403/404/422 เข้าด้วยกันโดยเจตนา: `403` ของกลุ่มนี้แปลว่า **Plant นี้อยู่นอก
 * ขอบเขตของคุณ** ซึ่งผู้ใช้แก้ได้ด้วยการเลือก Plant อื่นหรือไปขอสิทธิ์ ส่วน `404`
 * แปลว่าไม่มี product นี้ในมุมมองของคุณ — คนละเรื่องกันคนละครึ่ง
 */
export type FormulaFailure =
  | { readonly status: 'unauthenticated' }
  | { readonly status: 'forbidden'; readonly error: ApiErrorEnvelope }
  | { readonly status: 'not-found'; readonly error: ApiErrorEnvelope }
  | { readonly status: 'conflict'; readonly error: ApiErrorEnvelope }
  | { readonly status: 'rule-violation'; readonly error: ApiErrorEnvelope }
  | { readonly status: 'bad-request'; readonly error: ApiErrorEnvelope }
  | { readonly status: 'server-error'; readonly httpStatus: number }
  | { readonly status: 'network-error'; readonly detail: string }
  | { readonly status: 'invalid-response' }
  | { readonly status: 'cancelled' };

export type CurrentFormulaResult<TRow> =
  | { readonly status: 'ready'; readonly current: CurrentFormula<TRow> }
  | FormulaFailure;

export type FormulaRevisionsResult<TRow> =
  | { readonly status: 'ready'; readonly revisions: readonly FormulaRevision<TRow>[] }
  | FormulaFailure;

export type CreateFormulaResult<TRow> =
  | { readonly status: 'ready'; readonly revision: FormulaRevision<TRow> }
  | FormulaFailure;

export interface FormulaRequestOptions {
  /** base path ของ API (default: อ่านจาก `VITE_API_BASE_URL` แบบ same-origin) */
  readonly apiBaseUrl?: string;
  readonly signal?: AbortSignal;
  /**
   * inject ได้เพื่อการทดสอบ
   *
   * ค่าเริ่มต้นคือ `apiFetch` ตัวเดียวกับที่ client อื่นใช้ — header
   * `Authorization: Bearer` ถูกเติมที่นั่นจุดเดียว ไม่ใช่ที่ client แต่ละตัว
   */
  readonly fetchImpl?: typeof fetch;
}

/* -------------------------------------------------------------- parsing -- */

function requiredString(value: unknown): string | null {
  return typeof value === 'string' && value.trim() !== '' ? value : null;
}

function nullableString(value: unknown): string | null | undefined {
  if (value === null || value === undefined) {
    return null;
  }
  return typeof value === 'string' ? value : undefined;
}

/**
 * แถวทั้งชุดของ revision หนึ่งใบ
 *
 * คีย์ที่หายไปทั้งก้อนถือเป็นชุดว่าง (เซิร์ฟเวอร์อาจไม่ส่ง `items` มากับรายการ
 * revision ย้อนหลัง) แต่ค่าที่มาแล้วไม่ใช่ array หรือมีแถวผิดรูป = payload ผิดรูป —
 * สูตรที่แสดงไม่ครบคือสูตรที่คนอ่านจะผลิตของผิดตาม
 */
function parseRows<TRow>(spec: FormulaSpec<TRow>, raw: unknown): readonly TRow[] | null {
  if (raw === undefined || raw === null) {
    return [];
  }
  if (!Array.isArray(raw)) {
    return null;
  }
  const rows: TRow[] = [];
  for (const entry of raw) {
    const parsed = spec.parseRow(entry);
    if (parsed === null) {
      return null;
    }
    rows.push(parsed);
  }
  return rows;
}

export function parseFormulaRevision<TRow>(
  spec: FormulaSpec<TRow>,
  raw: unknown,
): FormulaRevision<TRow> | null {
  if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) {
    return null;
  }
  const record = raw as Record<string, unknown>;

  // `revision` เป็นจำนวนเต็มเสมอ และเป็นสิ่งที่ทั้งหน้าจอนี้มีไว้แสดง — ค่าที่อ่าน
  // ไม่ออกจึงเป็น payload ผิดรูป ไม่ใช่ revision หมายเลข 0
  const revision = record.revision;
  if (typeof revision !== 'number' || !Number.isInteger(revision)) {
    return null;
  }
  const validFrom = requiredString(record.valid_from);
  if (validFrom === null) {
    return null;
  }

  const formulaId = nullableString(record[spec.idKey]);
  const status = nullableString(record.status);
  const validTo = nullableString(record.valid_to);
  const plantCode = nullableString(record.plant_code);
  if (
    formulaId === undefined ||
    status === undefined ||
    validTo === undefined ||
    plantCode === undefined
  ) {
    return null;
  }

  const rows = parseRows(spec, record[spec.rowsKey]);
  if (rows === null) {
    return null;
  }

  return {
    formula_id: formulaId,
    revision,
    status,
    valid_from: validFrom,
    valid_to: validTo,
    plant_code: plantCode,
    rows,
  };
}

/**
 * คำตอบของ `GET .../bom` ซึ่งอาจแปลว่า "ยังไม่มี revision ที่ใช้อยู่"
 *
 * **contract freeze แล้ว (D-054)**: envelope คือ
 * `{ product_id, product_code, plant_code, current }` โดย `current` เป็น `null`
 * เมื่อยังไม่มีสูตร — นั่นคือ body ของ `200` ไม่ใช่ `404`
 *
 * เดิมโค้ดนี้แกะด้วยคีย์ `bom`/`routing` (ชื่อ path segment) เพราะตอนสร้าง contract
 * ยังไม่ได้กำหนดรูปนี้ ผลคืออ่าน `current` ไม่เจอแล้ว **รายงานว่า "ยังไม่มีสูตร"
 * ทั้งที่มีอยู่จริง** ซึ่งเป็นความผิดพลาดที่หน้าจอดูปกติทุกอย่าง — ตรึงไว้แล้วที่
 * `contractShape.test.ts` ด้วย body จริงที่ backend ส่ง
 *
 * สิ่งที่ **ไม่** ถูกอ่านว่า "ไม่มี": object ที่มี `revision` อยู่แต่อ่านไม่ครบ —
 * นั่นคือ payload ผิดรูป และการกลืนมันเป็นหน้าจอว่างจะชวนให้ผู้ใช้เขียน revision
 * ทับสูตรที่มีอยู่จริง
 */
export function parseCurrentFormula<TRow>(
  spec: FormulaSpec<TRow>,
  raw: unknown,
): CurrentFormula<TRow> | null {
  let payload = raw;
  if (typeof payload === 'object' && payload !== null && !Array.isArray(payload)) {
    const record = payload as Record<string, unknown>;
    // `current` คือคีย์ที่ contract กำหนด ส่วน path segment เก็บไว้เป็นทางถอย
    // สำหรับ fixture เก่าที่เขียนไว้ก่อน D-054 เท่านั้น
    if ('current' in record) {
      payload = record.current;
    } else if (spec.pathSegment in record) {
      payload = record[spec.pathSegment];
    }
  }

  if (payload === null || payload === undefined) {
    return { present: false };
  }
  if (typeof payload !== 'object' || Array.isArray(payload)) {
    return null;
  }

  const record = payload as Record<string, unknown>;
  const noRevision = record.revision === null || record.revision === undefined;
  const noId = record[spec.idKey] === null || record[spec.idKey] === undefined;
  if (noRevision && noId) {
    return { present: false };
  }

  const revision = parseFormulaRevision(spec, record);
  return revision === null ? null : { present: true, revision };
}

/**
 * รายการ revision ทั้งหมด เรียงจากใหม่ไปเก่า
 *
 * ⚠️ ด้วยเหตุผลเดียวกับ `parseCurrentFormula()` — contract บอกแค่ว่า endpoint นี้คืน
 * "ทุก revision ของ product+plant นั้น เรียงจากใหม่ไปเก่า" โดยไม่ได้ freeze envelope
 * ที่นี่จึงรับทั้ง array เปล่า ๆ และ envelope ที่ห่อด้วย `items`/`revisions`
 *
 * **ไม่เรียงลำดับซ้ำเอง**: contract ระบุว่าเซิร์ฟเวอร์เป็นผู้เรียง การเรียงซ้ำที่
 * frontend จะกลบกรณีที่เซิร์ฟเวอร์เรียงผิดจนไม่มีใครเห็น
 */
export function parseFormulaRevisionList<TRow>(
  spec: FormulaSpec<TRow>,
  raw: unknown,
): readonly FormulaRevision<TRow>[] | null {
  let rawList: unknown = raw;
  if (!Array.isArray(rawList) && typeof rawList === 'object' && rawList !== null) {
    const record = rawList as Record<string, unknown>;
    rawList = record.items ?? record.revisions;
  }
  if (!Array.isArray(rawList)) {
    return null;
  }

  const revisions: FormulaRevision<TRow>[] = [];
  for (const entry of rawList) {
    const parsed = parseFormulaRevision(spec, entry);
    if (parsed === null) {
      return null;
    }
    revisions.push(parsed);
  }
  return revisions;
}

function parseErrorEnvelope(raw: unknown): ApiErrorEnvelope {
  if (typeof raw !== 'object' || raw === null) {
    return EMPTY_ENVELOPE;
  }
  const record = raw as Record<string, unknown>;
  const details =
    typeof record.details === 'object' && record.details !== null
      ? (record.details as Record<string, unknown>)
      : {};

  return {
    error_code: requiredString(record.error_code),
    message: requiredString(record.message),
    rule: requiredString(details.rule),
    details,
  };
}

/* ------------------------------------------------------------- requests -- */

async function readEnvelope(response: Response): Promise<ApiErrorEnvelope> {
  try {
    return parseErrorEnvelope(await response.json());
  } catch {
    // error body ที่อ่านไม่ออกไม่ควรกลบสถานะ HTTP ที่อ่านออกแล้ว
    return EMPTY_ENVELOPE;
  }
}

type PayloadResult = { readonly status: 'payload'; readonly payload: unknown } | FormulaFailure;

async function requestPayload(
  path: string,
  init: RequestInit,
  options: FormulaRequestOptions,
): Promise<PayloadResult> {
  const fetchImpl = options.fetchImpl ?? apiFetch;
  if (typeof fetchImpl !== 'function') {
    return { status: 'network-error', detail: 'fetch ไม่พร้อมใช้งานใน runtime นี้' };
  }

  const baseUrl = options.apiBaseUrl ?? getConfiguredApiBaseUrl();
  const url = buildApiUrl(baseUrl, path);

  let response: Response;
  try {
    response = await fetchImpl(url, {
      credentials: 'same-origin',
      cache: 'no-store',
      ...init,
      ...(options.signal ? { signal: options.signal } : {}),
    });
  } catch (error) {
    if (options.signal?.aborted || (error as Error | undefined)?.name === 'AbortError') {
      return { status: 'cancelled' };
    }
    return {
      status: 'network-error',
      detail: error instanceof Error ? error.message : 'ไม่ทราบสาเหตุ',
    };
  }

  if (options.signal?.aborted) {
    return { status: 'cancelled' };
  }

  switch (response.status) {
    case 400:
      return { status: 'bad-request', error: await readEnvelope(response) };
    case 401:
      return { status: 'unauthenticated' };
    case 403:
      return { status: 'forbidden', error: await readEnvelope(response) };
    case 404:
      return { status: 'not-found', error: await readEnvelope(response) };
    case 409:
      return { status: 'conflict', error: await readEnvelope(response) };
    case 422:
      return { status: 'rule-violation', error: await readEnvelope(response) };
    default:
      break;
  }

  if (!response.ok) {
    return { status: 'server-error', httpStatus: response.status };
  }

  try {
    return { status: 'payload', payload: await response.json() };
  } catch {
    return { status: 'invalid-response' };
  }
}

/** `GET /v1/products/{product_id}/{bom|routing}` — revision ที่ใช้อยู่จริง */
export async function fetchCurrentFormula<TRow>(
  spec: FormulaSpec<TRow>,
  productId: string,
  plantCode: string,
  options: FormulaRequestOptions = {},
): Promise<CurrentFormulaResult<TRow>> {
  const result = await requestPayload(
    `${formulaPath(productId, spec.pathSegment)}${buildPlantQuery(plantCode)}`,
    { method: 'GET', headers: { Accept: 'application/json' } },
    options,
  );
  if (result.status !== 'payload') {
    return result;
  }

  const current = parseCurrentFormula(spec, result.payload);
  return current === null ? { status: 'invalid-response' } : { status: 'ready', current };
}

/** `GET /v1/products/{product_id}/{bom|routing}/revisions` — รวม revision ที่ปิดไปแล้ว */
export async function fetchFormulaRevisions<TRow>(
  spec: FormulaSpec<TRow>,
  productId: string,
  plantCode: string,
  options: FormulaRequestOptions = {},
): Promise<FormulaRevisionsResult<TRow>> {
  const result = await requestPayload(
    `${formulaRevisionsPath(productId, spec.pathSegment)}${buildPlantQuery(plantCode)}`,
    { method: 'GET', headers: { Accept: 'application/json' } },
    options,
  );
  if (result.status !== 'payload') {
    return result;
  }

  const revisions = parseFormulaRevisionList(spec, result.payload);
  return revisions === null
    ? { status: 'invalid-response' }
    : { status: 'ready', revisions };
}

/**
 * แปลง body ให้เหลือเฉพาะคีย์ที่ contract รับ — สามคีย์ และไม่มีคีย์ที่สี่
 *
 * **`revision` ออกไปไม่ได้เลย** ต่อให้ผู้เรียกถือ object ที่มีคีย์นั้นอยู่ในมือ:
 * เลข revision เป็นของเซิร์ฟเวอร์ และเลขที่ client เลือกเองคือการแข่งกันเขียนทับ
 * `UNIQUE(product_id, plant_id, revision)` (contract) ฟังก์ชันนี้เขียนคีย์ที่ออกไป
 * ทีละตัวด้วยเหตุผลเดียวกับ `serializeCreateProductBody()` — การ spread state ของ
 * ฟอร์มคือวิธีที่ฟิลด์ของเซิร์ฟเวอร์จะหลุดออกไปในวันที่มีคนเพิ่มช่องใหม่
 */
export function serializeCreateFormulaBody<TRow>(
  spec: FormulaSpec<TRow>,
  body: CreateFormulaBody<TRow>,
): Record<string, unknown> {
  return {
    plant_code: body.plant_code,
    effective_date: body.effective_date,
    [spec.rowsKey]: body.rows.map((row) => spec.serializeRow(row)),
  };
}

/**
 * `POST /v1/products/{product_id}/{bom|routing}` — สร้าง revision ใหม่ทั้งใบ
 *
 * ไม่ส่ง `Idempotency-Key`: contract สงวน header นั้นไว้ให้ Scan, Goods Receipt,
 * Initial Import และ Offline Sync ที่กระทบ stock/accounting การใส่กับกลุ่มนี้จะทำให้
 * ความหมายของ `request_dedup` เบลอ
 */
export async function createFormula<TRow>(
  spec: FormulaSpec<TRow>,
  productId: string,
  body: CreateFormulaBody<TRow>,
  options: FormulaRequestOptions = {},
): Promise<CreateFormulaResult<TRow>> {
  const result = await requestPayload(
    formulaPath(productId, spec.pathSegment),
    {
      method: 'POST',
      headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
      body: JSON.stringify(serializeCreateFormulaBody(spec, body)),
    },
    options,
  );
  if (result.status !== 'payload') {
    return result;
  }

  const revision = parseFormulaRevision(spec, result.payload);
  return revision === null ? { status: 'invalid-response' } : { status: 'ready', revision };
}
